import { BsBuilding, BsHeart, BsHouse, BsPeople } from 'react-icons/bs';
import type { FacilityType } from '@/app/_lib/parseFacilities';

interface FacilityIconProps {
  type: FacilityType;
  className?: string;
  variant?: 'filled' | 'outline';
}

export function getFacilityTypeColor(type: FacilityType): string {
  switch (type) {
    case 'corporate':
      return 'text-indigo-800 dark:text-indigo-400';
    case 'adult-residential':
      return 'text-teal-500 dark:text-teal-400';
    case 'elderly-residential':
      return 'text-amber-400 dark:text-amber-300';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
}

export function getFacilityTypeBgColor(type: FacilityType): string {
  switch (type) {
    case 'corporate':
      return 'bg-indigo-50 dark:bg-indigo-900/20';
    case 'adult-residential':
      return 'bg-teal-50 dark:bg-teal-900/20';
    case 'elderly-residential':
      return 'bg-amber-50 dark:bg-amber-900/20';
    default:
      return 'bg-gray-50 dark:bg-gray-900/20';
  }
}

export function FacilityIcon({ type, className = 'w-6 h-6', variant = 'filled' }: FacilityIconProps) {
  const colorClass = getFacilityTypeColor(type);
  const combinedClassName = className.includes('text-') ? className : `${className} ${colorClass}`;
  const fillClass = variant === 'filled' ? 'fill-current' : '';

  // For SVG contexts (like map markers), we need to return just the icon without div wrapper
  if (variant === 'outline') {
    switch (type) {
      case 'corporate':
        return <BsBuilding className={`${combinedClassName} ${fillClass}`} />;
      case 'adult-residential':
        return <BsPeople className={`${combinedClassName} ${fillClass}`} />;
      case 'elderly-residential':
        return <BsHeart className={`${combinedClassName} ${fillClass}`} />;
      default:
        return <BsHouse className={`${combinedClassName} ${fillClass}`} />;
    }
  }

  // For regular HTML contexts (like popups), we can use div wrapper for filled variant
  switch (type) {
    case 'corporate':
      return (
        <div className='relative'>
          <BsBuilding className={`${combinedClassName} ${fillClass}`} />
        </div>
      );
    case 'adult-residential':
      return (
        <div className='relative'>
          <BsPeople className={`${combinedClassName} ${fillClass}`} />
        </div>
      );
    case 'elderly-residential':
      return (
        <div className='relative'>
          <BsHeart className={`${combinedClassName} ${fillClass}`} />
        </div>
      );
    default:
      return (
        <div className='relative'>
          <BsHouse className={`${combinedClassName} ${fillClass}`} />
        </div>
      );
  }
}

export function getFacilityTypeLabel(type: FacilityType): string {
  switch (type) {
    case 'corporate':
      return 'Corporate Office';
    case 'adult-residential':
      return 'Adult Residential Facilities';
    case 'elderly-residential':
      return 'Residential Care Facilities for the Elderly';
    default:
      return 'Residential Facility';
  }
}

export function getFacilityTypeDescription(type: FacilityType): string {
  switch (type) {
    case 'corporate':
      return 'Our main training facility and corporate headquarters';
    case 'adult-residential':
      return 'Specialized residential care for adults with mental illness';
    case 'elderly-residential':
      return 'Residential care facilities dedicated to elderly residents';
    default:
      return 'Residential care facility';
  }
}
